/**
 * V3: Table of Contents
 *
 * Note: Presumes that TOC is ALWAYS the 2nd module (unless using the
 * `autoGenerateLinks` option, which is for blog posts and targets H2s)
 *
 * Template: v3\layouts\v3_table_of_contents.php
 *
 * @since #873
 * @link  https://in-beta.codebasehq.com/projects/on24-support/tickets/873  - Initial
 * @link  https://in-beta.codebasehq.com/projects/on24-support/tickets/1083 - Added blog support (`autoGenerateLinks`)
 *
 * @module modules/V3TableOfContents
 */


// Dependencies
// ============================================================================

import { $ } from '../common/utility/u-jquery-$';
import { UScroll } from '../common/utility/u-scroll';
import { UOptions } from '../common/utility/u-options';


// Vars
// ============================================================================

const debugLog = true;

const els = {
	$header:  $( '[data-v3-header-main]' ),
	$modules: $( '[data-v3-modules] .v3-module' ), // Modules ie. layouts. We'll loop over these to link to them later

	$wrap:    $( '[data-v3-toc-wrap]' ), // Main wrapping container
	$toc:     $( '[data-v3-toc-main]' ), // Main inner TOC container, is sticky, appears twice (desktop/mobile)
};

// Add options to `[data-v3-toc-wrap]`
let options = {
	lastModuleNum:       -1,    // Set in the file: v3_table_of_contents.php

	// Automatic stuff
	autoGenerateLinks:   false, // true to generate TOC panel links based on H2s. Requires targetHeadings=true
	targetHeadings:      false, // true to target H2s
	hasCustomLinks:      true,  // true if the user has set up custom links. If true, autoGenerateLinks is ignored
	autoNumberAutoLinks: false, // true to add numbers (1., 2., 3.) before the links in the TOC links panel
};


// ƒs
// ============================================================================

/**
 * Core module init
 *
 * @return  {void}
 */
function _moduleInit()
{
	if ( !els.$wrap.length )
	{
		return;
	}

	// Update the local `options` var with settings from the data attr
	options = UOptions.parseOptions( els.$wrap, 'data-v3-toc-wrap', options, 'V3-TOC' );


	// Setup anchors & links
	// ------------------------------------------------------------------------

	// Prevent the automatic link generation if the user set any custom links in the post
	if ( options.hasCustomLinks && options.autoGenerateLinks )
	{
		options.autoGenerateLinks = false;
	}

	if ( options.targetHeadings )
	{
		// Replace the original target with H2 headings
		els.$modules = $( '[data-v3-toc-headings-container] h2:not("[data-toc-ignore]")' );

		// Automatically add anchors (`id=""`) to headings
		autoAddAnchorsToHeadings();
	}

	addAnchorsToPanelLinks(); // Add anchor links & smoothscroll clicks


	// Interactions & Movement
	// ------------------------------------------------------------------------

	toggleBtns(); // Open/close panels
	makeSticky(); // Make panel sticky
	tocMobile();  // Mobile stuff

	$( window ).on( 'scroll', () =>
	{
		makeSticky();
	});


	// Misc Updates
	// ------------------------------------------------------------------------

	updateInitialMobileActiveText();
}


/**
 * Setup the toggle button, which opens/closes the TOC panel
 *
 * @return  {void}
 */
function toggleBtns()
{
	const $toggles        = els.$toc.find( '[data-v3-toc-toggle]' );
	const $inactivePanels = els.$toc.find( '[data-v3-toc-panel="inactive"]' );
	const $activePanels   = els.$toc.find( '[data-v3-toc-panel="active"]' );

	$toggles.on( 'click', ( ev ) =>
	{
		const $toggle    = $( ev.currentTarget );
		const toggleType = $toggle.attr( 'data-v3-toc-toggle' );

		switch( toggleType )
		{
			// Open the active (links) panel
			case 'open':
				$activePanels.addClass( 'is-active' );
				$inactivePanels.removeClass( 'is-active' );
				break;

			// Open the inactive (button) panel
			case 'close':
				$inactivePanels.addClass( 'is-active' );
				$activePanels.removeClass( 'is-active' );
				break;
		}
	});
}


/**
 * Automatically add anchors. Only used to add anchors to H2s, if
 * `autoGenerateLinks` is enabled in the options.
 *
 * Works by checking the post content for H2 headings, and adding anchors
 * (`id=""`) to each of them. Does not affect the links in the TOC panel
 *
 * @TODO: Check `lastModuleNum`, it might need updating dynamically here, otherwise
 * it's set in the code (v3_table_of_contents.php)
 *
 * @return  {void}
 */
function autoAddAnchorsToHeadings()
{
	// If targetting H2s, add IDs to each one. The H2s will act like sections
	els.$modules.each( ( i, el ) =>
	{
		const $h2 = $( el );
		const h2Text = $h2.text();
		let anchorText = h2Text;

		// Replace all characters except A-Z, 0-9, and spces
		anchorText = anchorText.replaceAll( /[^A-z0-9 ]/g, "" );
		anchorText = anchorText.replaceAll( ' ', '-' );

		$h2.attr( 'id', anchorText );
	});

	options.lastModuleNum = els.$modules.length;
}


/**
 * Loop over modules and get their IDs, then apply those IDs as anchor links to
 * the TOC panel's links
 *
 * @return  {void}
 */
function addAnchorsToPanelLinks()
{
	// Store of anchors found in the main content
	let anchorsArr = [];

	// Get module IDs
	els.$modules.each( ( i, el ) =>
	{
		const $module = $( el );
		const moduleID = $module.attr( 'id' );

		// Add to array and object
		anchorsArr.push( moduleID );
	});

	// Update TOC links to point to anchors
	// Loops over the TOC panels (desktop/mobile)
	els.$toc.each( ( i, el ) =>
	{
		const $thisToc = $( el ); // separate TOCs for desktop/mobile


		// OPTION 1: Auto Generated Links
		// --------------------------------------------------------------------
		// Generate links from H2s

		if ( options.autoGenerateLinks )
		{
			const $linksList = $thisToc.find( '[data-v3-toc-autolinks-list]' );
			const template   = $linksList.html();

			// Remove the template code from the DOM
			$linksList.html( '' );

			// Loop over the list of anchors
			let num = 1;
			anchorsArr.forEach( ( anchorID ) =>
			{
				const $heading = $( `[id="${anchorID}"]` );

				// Duplicate the template code, ready to customise
				let markup = template;

				const replacements = {
					module_num:  -1, // unused
					link_text:   $heading.text(),
					link_anchor: '#' + anchorID,
					link_num:    '', // can be set with the `autoNumberAutoLinks` option
				}

				// Option to automatically number the links/headings
				if ( options.autoNumberAutoLinks )
				{
					replacements.link_num =  num + '. ';
				}

				for ( const [search, replace] of Object.entries( replacements ) )
				{
					markup = markup.replaceAll( '{{' + search + '}}', replace );
				}

				// console.log( {markup, replacements} ); //@debug

				// Add the dynamic markup to the list (`autolinks-list`)
				$linksList.append( markup );

				num++;
			});


			const $links = $thisToc.find( '[data-v3-toc-link]' );

			// Similar to the code below (for non-auto generated links)
			// but only needs to activate smooth scrolling on the new links
			$links.each( ( i, el ) =>
			{
				const $link  = $( el );
				smoothScrollToAnchor( $link );
			});

			if ( debugLog )
			{
				console.log( '[V3-TOC] ⚓ Updated anchors (auto-generated)' );
			}
		}

		// OPTION 2: Manually added links
		// --------------------------------------------------------------------
		// Otherwise, just update the existing links in the TOC panel to add
		// the anchors (this is the default approach, with manually-added links in the CMS)

		else
		{
			const $links = $thisToc.find( '[data-v3-toc-link]' );

			$links.each( ( i, el ) =>
			{
				const $link  = $( el );
				const num    = $link.attr( 'data-v3-toc-link' );
				const anchor = `#${anchorsArr[num - 1]}`;

				$link.attr( 'href', anchor );

				smoothScrollToAnchor( $link );
			});

			if ( debugLog )
			{
				console.log( '[V3-TOC] ⚓ Updated anchors' );
			}
		}
	});
}


/**
 * Smoothly scroll to the target, accounting for the height of the header
 *
 * Borrowed from: legacy/l-smoothscroll.js
 *
 * @return  {void}
 */
function smoothScrollToAnchor( $link )
{
	const $inactivePanels = els.$toc.find( '[data-v3-toc-panel="inactive"]' );
	const $activePanels   = els.$toc.find( '[data-v3-toc-panel="active"]' );

	$link.on( 'click', ( ev ) =>
	{
		ev.preventDefault();

		const headerHeight = els.$header.outerHeight();
		const anchor       = $link.attr( 'href' );
		const $target      = $( anchor );
		const isMobile     = Boolean( $link.attr( 'data-v3-toc-mobile-link' ) );

		let scrollTopPos = $target.offset().top - headerHeight;

		if ( isMobile )
		{
			scrollTopPos -= 20; // accounts for the sticky TOC on mobile
		}
		else
		{
			// Potentially close the TOC if the viewport isn't wide enough
			if ( matchMedia( '(max-width: 1800px)' ).matches )
			{
				$inactivePanels.addClass( 'is-active' );
				$activePanels.removeClass( 'is-active' );
			}
		}

		$( 'html, body' ).animate({
			scrollTop: scrollTopPos,
		}, 300);
	});
}


/**
 * Make the panels sticky, when the conditions have been met
 *
 * @return  {void}
 */
function makeSticky()
{
	// We measure the wrapper's position ($wrap), rather than the actual
	// TOC, because the wrapper position isn't affected by our code
	const elPos         = UScroll.getElementPositions( els.$wrap[0] );

	const headerHeight  = els.$header.outerHeight();
	const activeCls     = 'is-sticky';

	// This is the last module that the TOC targets, after this is stops being sticky
	const lastModuleNum = options.lastModuleNum;
	const $lastModule   = els.$modules.eq(lastModuleNum - 1);

	if ( !$lastModule.length )
	{
		console.error( '[V3-TOC] Could not find $lastModule object', { lastModuleNum, $lastModule, $modules: els.$modules, options } );
		return;
	}

	// Target each $toc, because there's both desktop & mobile versions
	els.$toc.each( ( i, el ) =>
	{
		const $thisToc = $( el );
		const tocType  = $thisToc.attr( 'data-v3-toc-main' );

		let distance = headerHeight;

		switch( tocType )
		{
			case 'desktop':
				distance += 60;
				break;

			case 'mobile':
				distance += 10;
				break;
		}

		if ( elPos.distanceTop <= ( distance + 5 ) )
		{
			// Scrolled down past header
			$thisToc.addClass( activeCls );
			$thisToc.css( { 'top': `${distance}px` } );

			// Make unsticky again if we've past the last module.
			// Kicks in after 80% of the module has been scrolled past
			const lastModulePos = UScroll.getElementPositions( $lastModule[0] );

			if ( lastModulePos.percentBottom > 0.8 )
			{
				$thisToc.removeClass( activeCls );
				$thisToc.css( { 'top': 0 } );
			}
		}
		else
		{
			// Not past header yet, or scrolled back up to header
			if ( els.$toc.hasClass( activeCls ) )
			{
				els.$toc.removeClass( activeCls );
				els.$toc.css( { 'top': 0 } );
			}
		}
	});

}


/**
 * TOC: Mobile actions
 *
 * @return  {void}
 */
function tocMobile()
{
	// TOC: Mobile
	const $mobToggle      = $( '[data-v3-toc-mobile-menu-toggle]' );
	const $mobCurrentText = $( '[data-v3-toc-mobile-menu-current-text]' );
	const $mobMenu        = $( '[data-v3-toc-mobile-menu-dropdown]' );
	const $mobLinks       = $mobMenu.find( '[data-v3-toc-link]' );

	// Toggle dropdown
	$mobToggle.on( 'click', () =>
	{
		$mobToggle.toggleClass( 'is-active' );
		$mobMenu.toggleClass( 'is-active' );
	});

	// When the user clicks an item:
	//   - Update current text (which shows the currently active link)
	//   - Close dropdown
	$mobLinks.on( 'click', ( ev ) =>
	{
		const $item = $( ev.currentTarget );
		const itemText = $item.text().trim();

		// Update text
		$mobCurrentText.text( itemText );

		// Close menu
		$mobToggle.removeClass( 'is-active' );
		$mobMenu.removeClass( 'is-active' );
	});
}


/**
 * MOBILE: On initial load, update the text that shows the currently-active
 * TOC link item
 *
 * @return  {void}
 */
function updateInitialMobileActiveText()
{
	// Get the current URL hash, if applicable. We'll try to find the link
	// that it corresponds to, and make it active
	const hash = window.location.hash;

	const $links = els.$toc.find( '[data-v3-toc-link]' );
	const $activeTextEl = $( '[data-v3-toc-mobile-menu-current-text]' );

	let newText  = '';

	// We should never have this occur, but you never know...
	if ( !$links.length )
	{
		return;
	}

	if ( hash === '' )
	{
		// ! No hash...
		// Get the first menu item's text
		const $itemTextObj = $links.eq( 0 );

		newText = $itemTextObj.text();
	}
	else
	{
		// ! A hash appears in the URL...
		// Check if any links match that hash
		const $itemTextObj = $links.filter( `[href^="${hash}"]` ).eq( 0 );

		console.log({'$itemTextObj':$itemTextObj});

		newText = $itemTextObj.text();
	}

	$activeTextEl.text( newText );
}


// Auto Init
// ============================================================================

$( document ).ready( () => _moduleInit() );
